import { genericMemo } from "@redotech/react-util/component";
import {
  NumberComparisonOperator,
  numberComparisonOperators,
} from "@redotech/redo-flows-core-common/comparison-operators";
import {
  NumberComparisonExpression,
  numberComparisonToLabel,
} from "@redotech/redo-flows-core-schema-common/expressions/number-expression";
import { Schema } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { DeepPartial, DeepPartialExcept } from "@redotech/util/type";
import { useEffect, useMemo, useState } from "react";
import { useDelayedAutoFocus } from "../common/use-delayed-auto-focus";

const numberComparisonOperatorItems: RedoListItem<NumberComparisonOperator>[] =
  numberComparisonOperators.map((operator) => ({
    id: operator,
    type: "text",
    text: numberComparisonToLabel[operator],
    value: operator,
  }));

export const NumberInspector = genericMemo(function NumberInspector({
  schema,
  expression,
  setExpression,
  showValidationErrors,
}: {
  schema: Schema;
  expression: DeepPartialExcept<NumberComparisonExpression, "field">;
  setExpression: (expression: DeepPartial<NumberComparisonExpression>) => void;
  showValidationErrors?: boolean;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!expression.type || !expression.operator) {
      setExpression({
        ...expression,
        type: "number_comparison",
        operator: expression.operator ?? "greaterThan",
      });
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expression]);

  const selectedItem = numberComparisonOperatorItems.find(
    (item) => item.id === expression.operator,
  );

  const [touched, setTouched] = useState(showValidationErrors || false);
  const delayedAutoFocus = useDelayedAutoFocus(
    expression.comparisonValue === undefined,
  );

  const errorText = useMemo(() => {
    if (expression.comparisonValue === undefined && touched) {
      return t("A value is required");
    }
    return undefined;
  }, [expression.comparisonValue, touched, t]);

  return (
    <>
      <RedoSingleSelectDropdownInput<NumberComparisonOperator>
        label={t("Comparison")}
        options={numberComparisonOperatorItems}
        optionSelected={({ item: operator }) => {
          setExpression({ ...expression, operator: operator.value });
        }}
        selectedItem={selectedItem}
        size="xs"
      />

      <RedoTextInput
        autoFocus={delayedAutoFocus}
        description={errorText}
        inputTypeHint="number"
        key={delayedAutoFocus.toString()}
        label={t("Value")}
        onBlur={() => setTouched(true)}
        setValue={(value) => {
          const numValue = value === "" ? undefined : parseFloat(value);
          setExpression({ ...expression, comparisonValue: numValue });
        }}
        state={errorText ? "error" : "default"}
        value={expression.comparisonValue?.toString() ?? ""}
      />
    </>
  );
});
